import { Link, LinkProps } from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";

/**
 * Mapa de rotas públicas → import dinâmico do chunk.
 * Mantém o prefetch fora do caminho crítico de render (só dispara em hover/foco/idle).
 */
const loaders: Record<string, () => Promise<unknown>> = {
  "/blog": () => import("@/pages/Blog"),
  "/blog/planilha-vs-aplicativo-controle-financeiro": () =>
    import("@/pages/BlogMetodos"),
  "/sobre": () => import("@/pages/Sobre"),
  "/auth": () => import("@/pages/Auth"),
};

const prefetched = new Set<string>();

export function prefetchRoute(path: string) {
  if (prefetched.has(path)) return;
  const loader = loaders[path];
  if (!loader) return;
  prefetched.add(path);
  loader().catch(() => prefetched.delete(path));
}

/** Agenda um prefetch quando o browser estiver ocioso (não compete com o LCP). */
export function useIdlePrefetch(paths: string[]) {
  const key = paths.join("|");
  useEffect(() => {
    const run = () => key.split("|").filter(Boolean).forEach(prefetchRoute);
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) {
      const id = ric(run, { timeout: 3000 });
      return () => (window as unknown as { cancelIdleCallback?: (i: number) => void })
        .cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(run, 2500);
    return () => window.clearTimeout(t);
  }, [key]);
}

type Props = LinkProps & { to: string };

/** <Link> que carrega o chunk da rota assim que o usuário demonstra intenção. */
export const PrefetchLink = ({ to, onMouseEnter, onFocus, onTouchStart, ...rest }: Props) => {
  const done = useRef(false);
  const warm = useCallback(() => {
    if (done.current) return;
    done.current = true;
    prefetchRoute(to);
  }, [to]);

  return (
    <Link
      to={to}
      onMouseEnter={(e) => {
        warm();
        onMouseEnter?.(e);
      }}
      onFocus={(e) => {
        warm();
        onFocus?.(e);
      }}
      onTouchStart={(e) => {
        warm();
        onTouchStart?.(e);
      }}
      {...rest}
    />
  );
};
